/**
 * Waschma Cookie-Consent v3
 *
 * Integriert:
 *   - Google Consent Mode v2 (GA4, Google Ads)
 *   - Meta Pixel Consent API (fbq)
 *   - Shopify Customer Privacy API (Shop-Analytics, Web Pixels Manager)
 *
 * 3 Kategorien: Notwendig (immer aktiv) / Analyse / Marketing
 * Speicherung: localStorage mit Version-Bump (invalidiert alte Consents bei Update).
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'wc_consent';
  const UUID_KEY = 'wc_uuid';
  const VERSION = '1.0';

  // Consent-Log Endpoint: Supabase Edge Function (DSGVO Art. 7 Nachweispflicht).
  // Kein Supabase-Key im Frontend — Function macht Auth intern mit Service-Role.
  // CORS-Whitelist auf Function-Seite (nur waschma.com + Shopify-Domains).
  // Fehler beim Log NIE die UX blockieren (silent fail).
  const CONSENT_LOG_URL =
    'https://wmtpnzzombucbjmklxql.supabase.co/functions/v1/consent-log';

  /**
   * Get or create a pseudonymous UUID for this browser (localStorage-persisted).
   * Used as the sole identifier in consent_log — no PII sent to Supabase.
   */
  function getOrCreateUuid() {
    try {
      let uuid = localStorage.getItem(UUID_KEY);
      if (!uuid) {
        uuid = generateUuid();
        localStorage.setItem(UUID_KEY, uuid);
      }
      return uuid;
    } catch (e) {
      return null;
    }
  }

  function generateUuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    // Fallback for older browsers (RFC 4122 v4)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /**
   * Log the consent decision to Supabase for GDPR Art. 7 compliance proof.
   * actionType must match consent_log.action_type CHECK constraint:
   *   'accept_all' | 'necessary_only' | 'custom_save' | 'settings_reopen'
   * Never throws — silent fail so consent UX is never blocked by network errors.
   */
  function logConsentToSupabase(actionType, preferences) {
    try {
      const uuid = getOrCreateUuid();
      if (!uuid) return;

      const payload = {
        event_uuid: uuid,
        event_timestamp: new Date().toISOString(),
        banner_version: VERSION,
        action_type: actionType,
        analytics_granted: !!preferences.analytics,
        marketing_granted: !!preferences.marketing,
        page_path: (window.location.pathname || '').slice(0, 200),
        locale: (document.documentElement.lang || 'de').slice(0, 10)
      };

      fetch(CONSENT_LOG_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        // keepalive: Request survives page-navigation (User klickt & navigiert weg)
        keepalive: true,
        // Kein Cookie-Mitgeben — Endpoint braucht keine Auth
        credentials: 'omit'
      }).catch(function () {
        /* silent fail — Compliance-Log-Fehler dürfen UX nicht brechen */
      });
    } catch (e) {
      /* defensive silent fail */
    }
  }

  /**
   * Update all connected consent mechanisms based on user preferences.
   */
  function updateConsent(preferences) {
    // ── 1. Google Consent Mode v2 ─────────────────────────────
    if (typeof gtag === 'function') {
      gtag('consent', 'update', {
        ad_storage: preferences.marketing ? 'granted' : 'denied',
        ad_user_data: preferences.marketing ? 'granted' : 'denied',
        ad_personalization: preferences.marketing ? 'granted' : 'denied',
        analytics_storage: preferences.analytics ? 'granted' : 'denied'
      });
    }

    // ── 2. Meta Pixel Consent + Modeling-Mode ─────────────────
    // Sprint 022a — Package 2: LDU (Limited Data Use) für Modeling bei Denial.
    // Marketing = ON → normaler Meta-Track mit Cookies + Advanced Matching.
    // Marketing = OFF → LDU-Signal: Meta nutzt PageView + IP nur für aggregate
    //                    Modeling, keine Cookies, kein individueller User-Track.
    // Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse
    // für Ad-Optimierung ohne User-Identifikation).
    if (typeof fbq === 'function') {
      fbq('consent', preferences.marketing ? 'grant' : 'revoke');
      if (preferences.marketing) {
        // LDU aufheben — Meta darf voll tracken (mit Consent)
        fbq('dataProcessingOptions', []);
      } else {
        // LDU aktiv — nur Modeling-Signal, keine Cookies
        // 1 = EU-Country, 1000 = Wien (LDU Location Code)
        fbq('dataProcessingOptions', ['LDU'], 1, 1000);
      }
    }

    // ── 3. Shopify Customer Privacy API ───────────────────────
    // Sprint 022c — FIX: API muss erst via loadFeatures geladen werden bevor
    // setTrackingConsent funktioniert. Vorher wurde der Aufruf silent verschluckt
    // → _tracking_consent Cookie wurde NIE gesetzt → Meta-Pixel im Checkout hatte
    // keinen Consent-Signal → Purchase-Events fehlten.
    applyShopifyConsent(preferences);
  }

  function applyShopifyConsent(preferences) {
    const consent = {
      analytics: !!preferences.analytics,
      marketing: !!preferences.marketing,
      preferences: true,
      sale_of_data: !!preferences.marketing
    };

    function doApply() {
      if (!window.Shopify || !window.Shopify.customerPrivacy) return false;
      try {
        window.Shopify.customerPrivacy.setTrackingConsent(consent, function () {
          /* callback intentionally empty — cookie is set synchronously */
        });
        return true;
      } catch (e) {
        return false;
      }
    }

    // Try immediate — if API already loaded
    if (doApply()) return;

    // Otherwise: load Shopify Consent-Tracking-API feature first
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        function (error) {
          if (!error) doApply();
        }
      );
      return;
    }

    // Fallback: wait for Shopify object to appear (max 3s)
    let attempts = 0;
    const interval = setInterval(function () {
      attempts++;
      if (doApply() || attempts >= 30) {
        clearInterval(interval);
      }
    }, 100);
  }

  /**
   * Sprint 022b — TTL-Split für Consent-Persistenz:
   *   - "Alle akzeptieren" → 12 Monate (Standard-Praxis)
   *   - "Nur notwendig" → 30 Tage (Marcel-Entscheidung — Grauzone, riskanter als 3-6 Mo)
   * Wenn TTL abgelaufen: Banner erscheint erneut → User erhält frische Wahl.
   * Rechtsgrundlage: EDPB Cookie Guidelines 2023 (Refresh-Recht des Users).
   * Risiko: 30 Tage ist NoyB-Ziel — bei Abmahnung auf 90 Tage rauf.
   */
  const TTL_ACCEPT_ALL_MS = 365 * 24 * 60 * 60 * 1000;   // 12 Monate
  const TTL_NECESSARY_ONLY_MS = 30 * 24 * 60 * 60 * 1000; // 30 Tage

  /**
   * Load stored consent from localStorage. Returns null if none, version mismatch,
   * OR TTL abgelaufen (dann Banner erscheint erneut).
   */
  function getStoredConsent() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (data.version !== VERSION) return null;
      // TTL-Check: unterschiedliche Dauer je Consent-Wahl
      const age = Date.now() - (data.timestamp || 0);
      const ttl = data.marketing ? TTL_ACCEPT_ALL_MS : TTL_NECESSARY_ONLY_MS;
      if (age > ttl) {
        // Consent abgelaufen — als expired markieren, Banner erscheint neu
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  /**
   * Save consent to localStorage + push to all tracking mechanisms + Supabase-Log.
   * @param {object} preferences - {analytics, marketing}
   * @param {string} actionType - one of 'accept_all' | 'necessary_only' | 'custom_save'
   */
  function saveConsent(preferences, actionType) {
    const data = {
      version: VERSION,
      timestamp: Date.now(),
      necessary: true,
      analytics: !!preferences.analytics,
      marketing: !!preferences.marketing
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
    updateConsent(data);
    // GDPR Art. 7 Nachweispflicht: Log to Supabase (non-blocking)
    if (actionType) {
      logConsentToSupabase(actionType, preferences);
    }
  }

  /**
   * Render the bottom-bar consent banner.
   */
  function renderBanner() {
    const banner = document.createElement('div');
    banner.className = 'wc-cookie-banner';
    banner.setAttribute('role', 'dialog');
    banner.setAttribute('aria-label', 'Cookie-Einstellungen');
    banner.innerHTML = [
      '<div class="wc-cookie-banner__container">',
      '  <div class="wc-cookie-banner__text">',
      '    <strong>Diese Seite nutzt Cookies</strong>',
      '    Wir und ausgewählte Partner nutzen Cookies für Analyse, Marketing und personalisierte Inhalte. Notwendige Cookies sind immer aktiv. ',
      '    <a href="/policies/privacy-policy">Datenschutzerklärung</a>',
      '  </div>',
      '  <div class="wc-cookie-banner__buttons">',
      '    <button type="button" class="wc-btn wc-btn--link" data-wc-action="settings">Einstellungen</button>',
      '    <button type="button" class="wc-btn wc-btn--secondary" data-wc-action="necessary">Nur notwendige</button>',
      '    <button type="button" class="wc-btn wc-btn--primary" data-wc-action="all">Alle akzeptieren</button>',
      '  </div>',
      '</div>'
    ].join('');
    document.body.appendChild(banner);
    requestAnimationFrame(function () {
      banner.classList.add('is-visible');
    });
    return banner;
  }

  /**
   * Render the preferences modal with 3 toggles.
   */
  function renderModal(currentPrefs) {
    const modal = document.createElement('div');
    modal.className = 'wc-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-labelledby', 'wc-modal-title');
    modal.innerHTML = [
      '<div class="wc-modal__content">',
      '  <h2 id="wc-modal-title" class="wc-modal__title">Cookie-Einstellungen</h2>',
      '  <p class="wc-modal__text">Wir nutzen Cookies um die Funktionalität der Website zu gewährleisten, den Traffic zu analysieren und personalisierte Werbung anzuzeigen. Du kannst deine Präferenzen jederzeit ändern.</p>',
      '  <div class="wc-toggle">',
      '    <div class="wc-toggle__label">',
      '      <h3 class="wc-toggle__title">Notwendig</h3>',
      '      <p class="wc-toggle__desc">Für die grundlegende Funktion der Website unerlässlich (Warenkorb, Login, Sicherheit). Immer aktiv.</p>',
      '    </div>',
      '    <button type="button" class="wc-toggle__switch is-active is-disabled" aria-label="Notwendige Cookies (immer aktiv)" disabled></button>',
      '  </div>',
      '  <div class="wc-toggle">',
      '    <div class="wc-toggle__label">',
      '      <h3 class="wc-toggle__title">Analyse</h3>',
      '      <p class="wc-toggle__desc">Google Analytics 4 hilft uns die Website zu verbessern (anonymisierte Nutzungsdaten).</p>',
      '    </div>',
      '    <button type="button" class="wc-toggle__switch ' +
        (currentPrefs.analytics ? 'is-active' : '') +
        '" data-wc-toggle="analytics" role="switch" aria-checked="' +
        !!currentPrefs.analytics +
        '"></button>',
      '  </div>',
      '  <div class="wc-toggle">',
      '    <div class="wc-toggle__label">',
      '      <h3 class="wc-toggle__title">Marketing</h3>',
      '      <p class="wc-toggle__desc">Meta Pixel, Google Ads, TikTok für personalisierte Werbung und Conversion-Tracking.</p>',
      '    </div>',
      '    <button type="button" class="wc-toggle__switch ' +
        (currentPrefs.marketing ? 'is-active' : '') +
        '" data-wc-toggle="marketing" role="switch" aria-checked="' +
        !!currentPrefs.marketing +
        '"></button>',
      '  </div>',
      '  <div class="wc-modal__footer">',
      '    <button type="button" class="wc-btn wc-btn--secondary" data-wc-action="necessary">Nur notwendige</button>',
      '    <button type="button" class="wc-btn wc-btn--primary" data-wc-action="save">Auswahl speichern</button>',
      '  </div>',
      '</div>'
    ].join('');
    document.body.appendChild(modal);
    requestAnimationFrame(function () {
      modal.classList.add('is-visible');
    });
    return modal;
  }

  /**
   * Initialize consent system.
   */
  function init() {
    const stored = getStoredConsent();

    if (stored) {
      // Apply stored consent immediately, no banner needed.
      updateConsent(stored);
      // Expose global for re-opening from footer link.
      window.wcCookieOpenSettings = function () {
        const currentPrefs = { analytics: !!stored.analytics, marketing: !!stored.marketing };
        openModal(currentPrefs);
      };
      return;
    }

    // First visit: show banner + wait for user decision.
    const banner = renderBanner();
    let modal = null;
    const currentPrefs = { analytics: false, marketing: false };

    function openModal(prefs) {
      modal = renderModal(prefs);
    }

    function cleanup() {
      if (banner && banner.parentNode) banner.remove();
      if (modal && modal.parentNode) modal.remove();
      modal = null;
    }

    document.addEventListener('click', function (e) {
      const target = e.target.closest('[data-wc-action], [data-wc-toggle]');
      if (!target) return;

      const action = target.dataset.wcAction;
      const toggle = target.dataset.wcToggle;

      if (action === 'all') {
        saveConsent({ analytics: true, marketing: true }, 'accept_all');
        cleanup();
      } else if (action === 'necessary') {
        saveConsent({ analytics: false, marketing: false }, 'necessary_only');
        cleanup();
      } else if (action === 'settings') {
        openModal(currentPrefs);
      } else if (action === 'save') {
        saveConsent(currentPrefs, 'custom_save');
        cleanup();
      } else if (toggle && !target.disabled) {
        currentPrefs[toggle] = !currentPrefs[toggle];
        target.classList.toggle('is-active', currentPrefs[toggle]);
        target.setAttribute('aria-checked', String(currentPrefs[toggle]));
      }
    });

    // Global for re-opening the preferences modal from anywhere (e.g., footer link).
    window.wcCookieOpenSettings = function () {
      openModal(currentPrefs);
    };
  }

  // Run when DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
